import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { MathfieldElement } from "mathlive";
import { BlockMath } from "react-katex";
import "katex/dist/katex.min.css";

const QUESTIONS = [
  {
    "id": "q1",
    "instruction": "A group of students recorded how many books they read last month. The table shows their results. Find the median number of books read.",
    "valueLabel": "Books read last month",
    "frequencyLabel": "Number of students (frequency)",
    "rows": [
      [
        1,
        1
      ],
      [
        2,
        3
      ],
      [
        3,
        1
      ]
    ],
    "optionalWorking": false,
    "latex": "\\begin{array}{c|c}\\text{Books read last month} & \\text{Frequency} \\\\ \\hline 1 & 1 \\\\ 2 & 3 \\\\ 3 & 1 \\end{array}",
    "answers": [
      "2"
    ],
    "displayAnswer": "2"
  },
  {
    "id": "q2",
    "instruction": "A group of students recorded how many pets they have at home. The table shows their results. Find the median number of pets.",
    "valueLabel": "Number of pets",
    "frequencyLabel": "Number of students (frequency)",
    "rows": [
      [
        0,
        2
      ],
      [
        1,
        3
      ],
      [
        2,
        3
      ],
      [
        3,
        1
      ]
    ],
    "optionalWorking": false,
    "latex": "\\begin{array}{c|c}\\text{Number of pets} & \\text{Frequency} \\\\ \\hline 0 & 2 \\\\ 1 & 3 \\\\ 2 & 3 \\\\ 3 & 1 \\end{array}",
    "answers": [
      "1"
    ],
    "displayAnswer": "1"
  },
  {
    "id": "q3",
    "instruction": "A football team recorded the number of goals it scored in each match. Find the median number of goals scored.",
    "valueLabel": "Goals scored",
    "frequencyLabel": "Number of matches (frequency)",
    "rows": [
      [
        2,
        2
      ],
      [
        4,
        4
      ],
      [
        6,
        2
      ]
    ],
    "optionalWorking": true,
    "latex": "\\begin{array}{c|c}\\text{Goals scored} & \\text{Frequency} \\\\ \\hline 2 & 2 \\\\ 4 & 4 \\\\ 6 & 2 \\end{array}",
    "answers": [
      "4"
    ],
    "displayAnswer": "4"
  },
  {
    "id": "q4",
    "instruction": "Students were asked how many siblings they have. Find the median number of siblings.",
    "valueLabel": "Number of siblings",
    "frequencyLabel": "Number of students (frequency)",
    "rows": [
      [
        1,
        2
      ],
      [
        2,
        3
      ],
      [
        3,
        3
      ],
      [
        4,
        2
      ]
    ],
    "optionalWorking": true,
    "latex": "\\begin{array}{c|c}\\text{Number of siblings} & \\text{Frequency} \\\\ \\hline 1 & 2 \\\\ 2 & 3 \\\\ 3 & 3 \\\\ 4 & 2 \\end{array}",
    "answers": [
      "2.5",
      "\\frac{5}{2}"
    ],
    "displayAnswer": "2.5"
  },
  {
    "id": "q5",
    "instruction": "A café recorded how long customers waited for their orders. Find the median waiting time in minutes.",
    "valueLabel": "Waiting time (minutes)",
    "frequencyLabel": "Number of customers (frequency)",
    "rows": [
      [
        2,
        2
      ],
      [
        5,
        3
      ],
      [
        8,
        3
      ],
      [
        11,
        2
      ]
    ],
    "optionalWorking": true,
    "latex": "\\begin{array}{c|c}\\text{Waiting time (minutes)} & \\text{Frequency} \\\\ \\hline 2 & 2 \\\\ 5 & 3 \\\\ 8 & 3 \\\\ 11 & 2 \\end{array}",
    "answers": [
      "6.5",
      "\\frac{13}{2}"
    ],
    "displayAnswer": "6.5"
  },
  {
    "id": "q6",
    "instruction": "Students recorded how many times they visited the library last month. Find the median number of library visits.",
    "valueLabel": "Number of library visits",
    "frequencyLabel": "Number of students (frequency)",
    "rows": [
      [
        0,
        3
      ],
      [
        1,
        4
      ],
      [
        2,
        5
      ],
      [
        3,
        2
      ],
      [
        4,
        1
      ]
    ],
    "optionalWorking": false,
    "latex": "\\begin{array}{c|c}\\text{Number of library visits} & \\text{Frequency} \\\\ \\hline 0 & 3 \\\\ 1 & 4 \\\\ 2 & 5 \\\\ 3 & 2 \\\\ 4 & 1 \\end{array}",
    "answers": [
      "2"
    ],
    "displayAnswer": "2"
  },
  {
    "id": "q7",
    "instruction": "Students recorded how long their journeys to school took. Find the median journey time in minutes.",
    "valueLabel": "Journey time (minutes)",
    "frequencyLabel": "Number of students (frequency)",
    "rows": [
      [
        5,
        3
      ],
      [
        10,
        5
      ],
      [
        15,
        4
      ],
      [
        20,
        6
      ],
      [
        25,
        2
      ]
    ],
    "optionalWorking": false,
    "latex": "\\begin{array}{c|c}\\text{Journey time (minutes)} & \\text{Frequency} \\\\ \\hline 5 & 3 \\\\ 10 & 5 \\\\ 15 & 4 \\\\ 20 & 6 \\\\ 25 & 2 \\end{array}",
    "answers": [
      "15"
    ],
    "displayAnswer": "15"
  },
  {
    "id": "q8",
    "instruction": "A weather station recorded the temperature at 7 a.m. each day. Find the median temperature in degrees Celsius.",
    "valueLabel": "Temperature (?C)",
    "frequencyLabel": "Number of days (frequency)",
    "rows": [
      [
        -5,
        2
      ],
      [
        -2,
        4
      ],
      [
        0,
        3
      ],
      [
        3,
        2
      ],
      [
        6,
        1
      ]
    ],
    "optionalWorking": false,
    "latex": "\\begin{array}{c|c}\\text{Temperature (?C)} & \\text{Frequency} \\\\ \\hline -5 & 2 \\\\ -2 & 4 \\\\ 0 & 3 \\\\ 3 & 2 \\\\ 6 & 1 \\end{array}",
    "answers": [
      "-1"
    ],
    "displayAnswer": "-1"
  },
  {
    "id": "q9",
    "instruction": "A gardener measured the heights of a group of plants. Find the median height in metres.",
    "valueLabel": "Plant height (metres)",
    "frequencyLabel": "Number of plants (frequency)",
    "rows": [
      [
        1.2,
        2
      ],
      [
        1.4,
        3
      ],
      [
        1.6,
        3
      ],
      [
        1.8,
        2
      ],
      [
        2,
        2
      ]
    ],
    "optionalWorking": false,
    "latex": "\\begin{array}{c|c}\\text{Plant height (metres)} & \\text{Frequency} \\\\ \\hline 1.2 & 2 \\\\ 1.4 & 3 \\\\ 1.6 & 3 \\\\ 1.8 & 2 \\\\ 2 & 2 \\end{array}",
    "answers": [
      "1.6",
      "\\frac{8}{5}"
    ],
    "displayAnswer": "1.6"
  },
  {
    "id": "q10",
    "instruction": "Students recorded how long they spent reading on Saturday. Find the median reading time in minutes.",
    "valueLabel": "Reading time (minutes)",
    "frequencyLabel": "Number of students (frequency)",
    "rows": [
      [
        10,
        12
      ],
      [
        20,
        18
      ],
      [
        30,
        20
      ],
      [
        40,
        24
      ],
      [
        50,
        16
      ],
      [
        60,
        10
      ]
    ],
    "optionalWorking": false,
    "latex": "\\begin{array}{c|c}\\text{Reading time (minutes)} & \\text{Frequency} \\\\ \\hline 10 & 12 \\\\ 20 & 18 \\\\ 30 & 20 \\\\ 40 & 24 \\\\ 50 & 16 \\\\ 60 & 10 \\end{array}",
    "answers": [
      "35"
    ],
    "displayAnswer": "35"
  }
];

const STORAGE_KEY = "median_ungrouped_frequency_table_quiz_v2";
const SUBMIT_URL =
  `${process.env.REACT_APP_HOMEWORK_API_URL || (process.env.NODE_ENV === "development" ? "http://localhost:5001" : "")}/api/homework/median-from-an-ungrouped-frequency-table`;

function normalize(raw) {
  let value = String(raw ?? "")
    .toLowerCase()
    .replace(/\\left|\\right/g, "")
    .replace(/\\frac\{([^{}]+)\}\{([^{}]+)\}/g, "$1/$2")
    .replace(/\\cdot|\\times/g, "*")
    .replace(/\\div/g, "/")
    .replace(/\\,/g, "")
    .replace(/\s+/g, "")
    .replace(/[{}()]/g, "");

  return value.replace(/\*/g, "");
}

function makeSubmissionId() {
  if (
    window.crypto &&
    typeof window.crypto.randomUUID === "function"
  ) {
    return window.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function ResultIcon({ type, size = 28 }) {
  const isCorrect = type === "correct";
  const colour = isCorrect ? "#138a5b" : "#d64545";
  const paleColour = isCorrect ? "#e7f7ef" : "#fdecec";

  return (
    <svg
      aria-label={isCorrect ? "Correct" : "Incorrect"}
      role="img"
      width={size}
      height={size}
      viewBox="0 0 32 32"
      style={{ display: "inline-block", flexShrink: 0 }}
    >
      <circle cx="16" cy="16" r="14" fill={paleColour} stroke={colour} strokeWidth="2" />
      {isCorrect ? (
        <path
          d="M9.2 16.2l4.3 4.4 9.5-10"
          fill="none"
          stroke={colour}
          strokeWidth="3.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : (
        <path
          d="M11 11l10 10M21 11L11 21"
          fill="none"
          stroke={colour}
          strokeWidth="3.2"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
}

export default function MedianFromAnUngroupedFrequencyTableQuiz() {
  const navigate = useNavigate();
  const questions = useMemo(() => QUESTIONS, []);

  const [studentName, setStudentName] = useState("");
  const [nameConfirmed, setNameConfirmed] = useState(false);
  const [current, setCurrent] = useState(0);
  const [studentAnswer, setStudentAnswer] = useState("");
  const [working, setWorking] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [questionResults, setQuestionResults] = useState(Array(questions.length).fill(null));
  const [studentAnswers, setStudentAnswers] = useState(Array(questions.length).fill(""));
  const [workingAnswers, setWorkingAnswers] = useState(Array(questions.length).fill(""));
  const [attemptHistory, setAttemptHistory] = useState(Array.from({ length: questions.length }, () => []));
  const [optionalTableProgress, setOptionalTableProgress] = useState({});
  const [guidedProgress, setGuidedProgress] = useState({});
  const [reviewMode, setReviewMode] = useState(false);
  const [done, setDone] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState("idle");
  const [submissionError, setSubmissionError] = useState("");
  const [submissionId, setSubmissionId] = useState(makeSubmissionId);

  useEffect(() => {
    if (!customElements.get("math-field")) customElements.define("math-field", MathfieldElement);
  }, []);

  useEffect(() => {
    try {
      const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (!data) return;
      setStudentName(data.studentName || "");
      setNameConfirmed(Boolean(data.nameConfirmed));
      setCurrent(Number.isInteger(data.current) ? data.current : 0);
      if (Array.isArray(data.questionResults) && data.questionResults.length === questions.length) setQuestionResults(data.questionResults);
      if (Array.isArray(data.studentAnswers) && data.studentAnswers.length === questions.length) setStudentAnswers(data.studentAnswers);
      if (Array.isArray(data.workingAnswers) && data.workingAnswers.length === questions.length) setWorkingAnswers(data.workingAnswers);
      if (Array.isArray(data.attemptHistory) && data.attemptHistory.length === questions.length) setAttemptHistory(data.attemptHistory);
      setGuidedProgress(data.guidedProgress || {});
      setOptionalTableProgress(data.optionalTableProgress || {});
      setDone(Boolean(data.done));
      setSubmissionStatus(data.submissionStatus || "idle");
      setSubmissionError(data.submissionError || "");
      setSubmissionId(data.submissionId || makeSubmissionId());
      const index = Number.isInteger(data.current) ? data.current : 0;
      setStudentAnswer(data.studentAnswers?.[index] || "");
      setWorking(data.workingAnswers?.[index] || "");
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [questions.length]);

  const saveProgress = (override = {}) => {
    const payload = {
      studentName, nameConfirmed, current, questionResults, studentAnswers,
      workingAnswers, attemptHistory, guidedProgress, optionalTableProgress, done, submissionStatus, submissionError, submissionId, ...override,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  };

  const confirmName = (event) => {
    event.preventDefault();
    const cleanName = studentName.trim();
    if (!cleanName) return;
    setStudentName(cleanName);
    setNameConfirmed(true);
    saveProgress({ studentName: cleanName, nameConfirmed: true });
  };

  const checkAnswer = () => {
    if (!studentAnswer.trim()) {
      setFeedback("Please enter an answer first.");
      return;
    }

    const q = questions[current];
    const isCorrect = q.answers.some((answer) => normalize(answer) === normalize(studentAnswer));
    void playQuizFeedback(isCorrect);
    const updatedStudentAnswers = [...studentAnswers];
    const updatedWorkingAnswers = [...workingAnswers];
    const updatedHistory = attemptHistory.map((history) => [...history]);
    updatedStudentAnswers[current] = studentAnswer;
    updatedWorkingAnswers[current] = working;
    updatedHistory[current].push(studentAnswer);

    const updatedResults = [...questionResults];
    const newAttempts = attempts + 1;
    setAttempts(newAttempts);
    setStudentAnswers(updatedStudentAnswers);
    setWorkingAnswers(updatedWorkingAnswers);
    setAttemptHistory(updatedHistory);

    if (isCorrect) {
      updatedResults[current] = "correct";
      setQuestionResults(updatedResults);
      setFeedback(
        <div style={styles.statusLine}>
          <ResultIcon type="correct" />
          <strong style={{ color: "#087f5b" }}>Correct!</strong>
        </div>
      );
    } else if (newAttempts >= 3) {
      updatedResults[current] = "incorrect";
      setQuestionResults(updatedResults);
      setFeedback("Correct answer shown in blue. You can move on now.");
    } else {
      setFeedback(
        <div style={styles.statusLine}>
          <ResultIcon type="incorrect" />
          <strong style={{ color: "#c0392b" }}>
            Try again. Attempts left: {3 - newAttempts}
          </strong>
        </div>
      );
    }

    saveProgress({
      studentAnswers: updatedStudentAnswers,
      workingAnswers: updatedWorkingAnswers,
      attemptHistory: updatedHistory,
      questionResults: updatedResults,
    });
  };

  const isGuided = Boolean(GUIDED_MEDIAN_ROWS[questions[current].id]);
  const canAdvance = isGuided
    ? questionResults[current] === "correct"
    : questionResults[current] === "correct" || attempts >= 3;

  const updateOptionalTable = (values) => {
    const updated = { ...optionalTableProgress, [questions[current].id]: values };
    setOptionalTableProgress(updated);
    saveProgress({ optionalTableProgress: updated });
  };

  const updateGuidedProgress = (progress) => {
    const updated = { ...guidedProgress, [questions[current].id]: progress };
    setGuidedProgress(updated);
    saveProgress({ guidedProgress: updated });
  };

  const completeGuidedQuestion = (answer, progress) => {
    const updatedResults = [...questionResults];
    const updatedAnswers = [...studentAnswers];
    const updatedWorking = [...workingAnswers];
    const updatedHistory = attemptHistory.map((history) => [...history]);
    const updatedProgress = { ...guidedProgress, [questions[current].id]: progress };
    updatedResults[current] = "correct";
    updatedAnswers[current] = answer;
    updatedHistory[current].push(answer);
    updatedWorking[current] = "Cumulative frequencies: " + GUIDED_MEDIAN_ROWS[questions[current].id].map((_, index) => progress.values["cf" + index]).join(", ")
      + "; Total frequency: " + progress.values.total + "; Median position: " + progress.values.position + "; Median: " + answer;
    setQuestionResults(updatedResults);
    setStudentAnswers(updatedAnswers);
    setWorkingAnswers(updatedWorking);
    setAttemptHistory(updatedHistory);
    setStudentAnswer(answer);
    setWorking(updatedWorking[current]);
    setGuidedProgress(updatedProgress);
    saveProgress({ questionResults: updatedResults, studentAnswers: updatedAnswers, workingAnswers: updatedWorking, attemptHistory: updatedHistory, guidedProgress: updatedProgress });
  };

  const submitResults = async (results, answers, histories, workings) => {
    setSubmissionError("");
    setSubmissionStatus("sending");
    saveProgress({ done: true, questionResults: results, studentAnswers: answers, attemptHistory: histories, workingAnswers: workings, submissionStatus: "sending" });

    const payload = {
      submissionId,
      studentName: studentName.trim(),
      quizTitle: "Median from an ungrouped frequency table",
      completedAt: new Date().toISOString(),
      score: results.filter((result) => result === "correct").length,
      total: questions.length,
      questions: questions.map((question, index) => ({
        number: index + 1,
        question: `${question.instruction} ${question.latex}`,
        instruction: question.instruction,
        table: {
          valueLabel: question.valueLabel, frequencyLabel: question.frequencyLabel, rows: question.rows,
          cumulative: Boolean(GUIDED_MEDIAN_ROWS[question.id]) || question.optionalWorking,
          showTotal: question.optionalWorking || guidedProgress[question.id]?.step >= 1,
          values: guidedProgress[question.id]?.values || optionalTableProgress[question.id] || {},
          status: guidedProgress[question.id]?.status || {},
          guidedStep: guidedProgress[question.id]?.step,
        },
        correctAnswer: question.displayAnswer,
        studentAnswer: answers[index] || "No answer",
        attempts: guidedProgress[question.id]?.attempts?.length
          ? guidedProgress[question.id].attempts : histories[index] || [],
        working: workings[index] || "",
        result: results[index] || "incorrect",
      })),
    };

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const messages = {
          404: "The results service could not be found. Please let Ryan know.",
          503: "Email delivery is not configured yet. Please let Ryan know.",
          502: "The email service could not deliver your results. Please try again later.",
        };
        throw new Error(messages[response.status] || "The results service could not accept your results. Please try again later.");
      }
      setSubmissionStatus("sent");
      saveProgress({ done: true, submissionStatus: "sent", submissionError: "" });
    } catch (error) {
      const message = error instanceof TypeError
        ? "Cannot connect to the results service. Please let Ryan know or try again later."
        : error.message;
      setSubmissionError(message);
      setSubmissionStatus("failed");
      saveProgress({ done: true, submissionStatus: "failed", submissionError: message });
    }
  };

  const nextQuestion = () => {
    setFeedback(null);
    setAttempts(0);
    const next = current + 1;

    if (next < questions.length) {
      setCurrent(next);
      setStudentAnswer(studentAnswers[next] || "");
      setWorking(workingAnswers[next] || "");
      saveProgress({ current: next });
      return;
    }

    if (reviewMode) {
      navigate("/student-dashboard");
      return;
    }

    setDone(true);
    saveProgress({ done: true });
    if (submissionStatus !== "sent") {
      submitResults(questionResults, studentAnswers, attemptHistory, workingAnswers);
    }
  };

  const previousQuestion = () => {
    if (current === 0) return;
    const previous = current - 1;
    setCurrent(previous);
    setFeedback(null);
    setAttempts(attemptHistory[previous]?.length || 0);
    setStudentAnswer(studentAnswers[previous] || "");
    setWorking(workingAnswers[previous] || "");
    saveProgress({ current: previous });
  };

  const startReview = () => {
    setReviewMode(true);
    setCurrent(0);
    setFeedback(null);
    setStudentAnswer(studentAnswers[0] || "");
    setWorking(workingAnswers[0] || "");
  };

  const retrySubmission = () => submitResults(questionResults, studentAnswers, attemptHistory, workingAnswers);
  const score = questionResults.filter((result) => result === "correct").length;

  const feedbackMessage = () => {
    const percentage = (score / questions.length) * 100;
    if (percentage === 100) return "🌟 Perfect—you got everything right!";
    if (percentage >= 80) return "🎉 Great job—you did really well!";
    if (percentage >= 50) return "👍 Good effort. Review any red questions.";
    return "💪 Keep practising. Reviewing your answers will help.";
  };

  const renderProgress = () => (
    <div style={styles.progressWrapper}>
      {questionResults.map((result, index) => (
        <div key={questions[index].id} style={{
          ...styles.progressDot,
          backgroundColor: result === "correct" ? "#00b894" : result === "incorrect" ? "#d63031" : "#636e72",
          border: index === current && !done ? "3px solid #f9b710" : "2px solid transparent",
        }}>
          {index + 1}
        </div>
      ))}
    </div>
  );

  if (!nameConfirmed) {
    return (
  
        <div style={styles.container}>
          <div style={styles.nameCard}>
            <h1 style={styles.heading}>Median from an ungrouped frequency table Quiz</h1>
            <p>Please enter your name before you begin. Your results will be sent to Ryan when you finish.</p>
            <form onSubmit={confirmName}>
              <label htmlFor="student-name" style={styles.label}>Your name</label>
              <input id="student-name" autoFocus autoComplete="name" maxLength={80} value={studentName}
                onChange={(event) => setStudentName(event.target.value)} style={styles.nameInput} />
              <button type="submit" disabled={!studentName.trim()} style={styles.startBtn}>Start Quiz ➡️</button>
            </form>
          </div>
        </div>
   
    );
  }

  const q = questions[current];
  const currentResult = questionResults[current];
  const completedFieldStyle =
    currentResult === "correct"
      ? styles.correctField
      : currentResult === "incorrect"
        ? styles.incorrectField
        : styles.unansweredField;

  return (
       <div style={styles.container}>
        <h1 style={styles.heading}>Median from an ungrouped frequency table</h1>
        <p style={styles.studentLabel}>Student: <strong>{studentName}</strong></p>
        {renderProgress()}

        {done && !reviewMode ? (
          <div style={styles.resultsBox}>
            <h2 style={{ fontFamily: "Inter, 'Segoe UI', sans-serif", color: "#17324d", fontWeight: 700 }}>Score: {score} / {questions.length}</h2>
            <p style={styles.feedbackText}>{feedbackMessage()}</p>
            {submissionStatus === "sending" && <p>Sending your results to Ryan…</p>}
            {submissionStatus === "sent" && (
              <div style={{ ...styles.statusLine, ...styles.sentMessage }}>
                <ResultIcon type="correct" size={25} />
                <span>Your results have been sent to Ryan.</span>
              </div>
            )}
            {submissionStatus === "failed" && (
              <div>
                <p style={styles.errorMessage}>Your quiz is saved, but the results could not be sent.</p>
                {submissionError && <p role="alert">{submissionError}</p>}
                <button onClick={retrySubmission} style={styles.submitBtn}>Try Sending Again</button>
              </div>
            )}
            <button onClick={startReview} style={styles.reviewBtn}>🔍 Review Questions</button>
          </div>
        ) : (
          <div style={styles.card}>
            <h3 style={styles.questionHeading}>Question {current + 1} of {questions.length}</h3>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 24 }}>
              <p style={{ ...styles.instruction, flex: 1, margin: 0 }}>
                {q.instruction.split("Find the median")[0].trim()}
                <br />
                Find the median{q.instruction.split("Find the median")[1]}
              </p>
              <div style={{ display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
                <CalculatorIcon allowed={q.calculator === "yes"} />
              </div>
            </div>
            {isGuided && (guidedProgress[q.id] || (!reviewMode && !canAdvance)) ? (
              <GuidedMedianQuestion key={q.id} questionId={q.id} progress={guidedProgress[q.id]}
                readOnly={reviewMode || canAdvance} onChange={updateGuidedProgress} onComplete={completeGuidedQuestion} />
            ) : (
              <>
            <MedianFrequencyTable question={q} values={optionalTableProgress[q.id] || {}}
              readOnly={reviewMode || canAdvance} onChange={updateOptionalTable} />

            <label style={styles.label}>Show your working:</label>
            <textarea rows={4} readOnly={reviewMode || canAdvance}
              value={reviewMode ? workingAnswers[current] : working}
              onChange={(event) => setWorking(event.target.value)}
              placeholder="Show any working here" style={{ ...styles.workingInput, ...completedFieldStyle }} />

            <label style={styles.label}>Answer:</label>
            <math-field readOnly={reviewMode || canAdvance} style={{ ...styles.mathInput, ...completedFieldStyle, ...(questionResults[current] === "incorrect" ? { color: "#1d4ed8", backgroundColor: "#eff6ff", borderColor: "#60a5fa" } : {}) }}
              value={questionResults[current] === "incorrect" ? q.displayAnswer : reviewMode || canAdvance ? studentAnswers[current] : studentAnswer}
              onInput={(event) => !reviewMode && setStudentAnswer(event.target.value)} />

              </>
            )}

            {reviewMode && (
              <div style={styles.reviewAnswer}>
                <div style={styles.statusLine}>
                  <ResultIcon type={questionResults[current] === "correct" ? "correct" : "incorrect"} />
                  <strong style={{ color: questionResults[current] === "correct" ? "#087f5b" : "#c0392b" }}>
                    {questionResults[current] === "correct" ? "Correct:" : "Correct answer:"}
                  </strong>
                </div>
                <BlockMath math={q.displayAnswer} />
              </div>
            )}
            {!reviewMode && feedback && <div style={styles.feedbackBox}>{feedback}</div>}
            {!isGuided && !reviewMode && !canAdvance && <button onClick={checkAnswer} style={styles.submitBtn}>Check Answer</button>}

            {(canAdvance || reviewMode) && (
              <div>
                {current > 0 && <button onClick={previousQuestion} style={styles.secondaryBtn}>← Back</button>}
                <button onClick={nextQuestion} style={styles.nextBtn}>
                  {current + 1 < questions.length ? "Next ➡️" : reviewMode ? "Finish Review" : "Finish & Send 🎉"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    
  );
}

const styles = {
  container: { padding: "2rem", fontFamily: "'Fredoka', cursive", backgroundColor: "#eaf4fc", minHeight: "100vh" },
  heading: { fontFamily: "Inter, 'Segoe UI', sans-serif", fontSize: "clamp(1.6rem, 3vw, 2.25rem)", fontWeight: 700, lineHeight: 1.25, letterSpacing: "-0.035em", color: "#17324d", textAlign: "center", maxWidth: 760, margin: "0 auto 1.5rem", paddingBottom: "1rem", borderBottom: "3px solid #b8d4e8" },
  questionHeading: { fontFamily: "Inter, 'Segoe UI', sans-serif", fontSize: "1rem", fontWeight: 600, letterSpacing: "0.025em", color: "#475569", margin: "0 0 1.25rem" },
  studentLabel: { textAlign: "center", marginBottom: "1rem" },
  card: { backgroundColor: "#fff", padding: "2rem", borderRadius: 12, boxShadow: "0 4px 8px rgba(0,0,0,0.1)", maxWidth: 700, margin: "1rem auto 0" },
  nameCard: { backgroundColor: "#fff", padding: "2rem", borderRadius: 12, boxShadow: "0 4px 8px rgba(0,0,0,0.1)", maxWidth: 520, margin: "4rem auto" },
  nameInput: { display: "block", width: "100%", boxSizing: "border-box", padding: "0.75rem", margin: "0.5rem 0 1rem", border: "1px solid #aaa", borderRadius: 8, fontSize: "1.1rem" },
  startBtn: { padding: "0.7rem 1.3rem", backgroundColor: "#6c5ce7", color: "white", fontWeight: "bold", border: 0, borderRadius: 7, cursor: "pointer" },
  calculatorIndicator: { marginBottom: 12, display: "flex", justifyContent: "flex-end" },
  instruction: { fontWeight: 700, fontSize: "1.05rem", marginBottom: 24 },
  label: { fontFamily: "Inter, 'Segoe UI', sans-serif", color: "#334155", fontWeight: 600, marginTop: 10, display: "block" },
  mathInput: { display: "block", width: "100%", boxSizing: "border-box", fontSize: "1.25rem", padding: "0.65rem", borderRadius: 8, border: "1px solid #ccc", marginTop: 8 },
  workingInput: { width: "100%", boxSizing: "border-box", borderRadius: 8, border: "1px solid #ccc", padding: "0.75rem", fontSize: "1rem", fontFamily: "'Fredoka', cursive", marginTop: 8, marginBottom: 20 },
  unansweredField: { backgroundColor: "#fff", border: "1px solid #ccc" },
  correctField: { backgroundColor: "#e8f8ee", border: "2px solid #42a96b" },
  incorrectField: { backgroundColor: "#fdeaea", border: "2px solid #d95858" },
  feedbackBox: { marginTop: "1rem" },
  statusLine: { display: "flex", alignItems: "center", gap: 10 },
  reviewAnswer: { marginTop: "1rem", padding: "1rem", backgroundColor: "#f5f6fa", borderRadius: 8 },
  submitBtn: { marginTop: "1rem", padding: "0.6rem 1.2rem", backgroundColor: "#0984e3", color: "white", fontWeight: "bold", border: 0, borderRadius: 6, cursor: "pointer", marginRight: "0.75rem" },
  nextBtn: { marginTop: "1rem", padding: "0.6rem 1.2rem", backgroundColor: "#00b894", color: "white", fontWeight: "bold", border: 0, borderRadius: 6, cursor: "pointer" },
  secondaryBtn: { marginTop: "1rem", padding: "0.6rem 1.2rem", backgroundColor: "#636e72", color: "white", fontWeight: "bold", border: 0, borderRadius: 6, cursor: "pointer", marginRight: "0.75rem" },
  progressWrapper: { display: "flex", justifyContent: "center", gap: 10, marginBottom: "1.5rem", flexWrap: "wrap" },
  progressDot: { width: 36, height: 36, borderRadius: "50%", display: "flex", justifyContent: "center", alignItems: "center", fontWeight: "bold", color: "white" },
  resultsBox: { textAlign: "center", backgroundColor: "white", maxWidth: 700, margin: "2rem auto", padding: "2rem", borderRadius: 12, boxShadow: "0 4px 8px rgba(0,0,0,0.1)" },
  feedbackText: { fontSize: "1.25rem", margin: "1rem 0" },
  sentMessage: { color: "#087f5b", fontWeight: 700, justifyContent: "center", margin: "1rem 0" },
  errorMessage: { color: "#c0392b", fontWeight: 700 },
  reviewBtn: { marginTop: "1rem", padding: "0.6rem 1.2rem", backgroundColor: "#636e72", color: "white", fontWeight: "bold", border: 0, borderRadius: 6, cursor: "pointer" },
};


function CalculatorIcon({ allowed }) {
  const label = allowed ? "Calculator allowed" : "No calculator";

  return (
    <svg role="img" aria-label={label} width={56} height={40} viewBox="0 0 56 40"
      style={{ display: "block" }}>
      <title>{label}</title>
      <rect x="5" y="3" width="27" height="34" rx="4" fill="#f1f5f9" stroke="#334155" strokeWidth="2" />
      <rect x="10" y="8" width="17" height="7" rx="1" fill="#cbd5e1" stroke="#334155" />
      <path d="M11 21h2m5 0h2m5 0h2M11 27h2m5 0h2m5 0h2M11 32h2m5 0h2m5 0h2"
        stroke="#334155" strokeWidth="3" strokeLinecap="round" />
      {allowed ? (
        <path d="M37 22l5 5 9-12" fill="none" stroke="#138a5b" strokeWidth="4"
          strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <path d="M5 5l27 30M32 5L5 35" fill="none" stroke="#d64545" strokeWidth="4"
          strokeLinecap="round" />
      )}
    </svg>
  );
}

function MedianFrequencyTable({ question, values, readOnly, onChange }) {
  const blank = (key, label) => (
    <input type="text" inputMode="numeric" aria-label={label} value={values[key] || ""}
      readOnly={readOnly} style={guidedStyles.input}
      onChange={(event) => onChange({ ...values, [key]: event.target.value })} />
  );

  return (
    <section style={guidedStyles.section}>
      {question.optionalWorking && <p style={{ marginTop: 0, marginBottom: 24 }}>You can fill in the cumulative frequencies and total to help you.</p>}
      <div style={{ overflowX: "auto" }}>
        <table aria-label={question.valueLabel} style={guidedStyles.table}>
          <thead><tr>
            <th scope="col" style={guidedStyles.cell}>{question.valueLabel}</th>
            <th scope="col" style={guidedStyles.cell}>{question.frequencyLabel}</th>
            {question.optionalWorking && <th scope="col" style={guidedStyles.cell}>Cumulative frequency</th>}
          </tr></thead>
          <tbody>{question.rows.map(([value, frequency], index) => <tr key={value}>
            <th scope="row" style={guidedStyles.cell}>{value}</th>
            <td style={guidedStyles.cell}>{frequency}</td>
            {question.optionalWorking && <td style={guidedStyles.cell}>{blank(`cf${index}`, `Optional cumulative frequency for value ${value}`)}</td>}
          </tr>)}</tbody>
          {question.optionalWorking && <tfoot><tr>
            <th scope="row" style={guidedStyles.cell}>Total</th>
            <td style={guidedStyles.cell}>{blank("total", "Optional total frequency")}</td>
            <td style={guidedStyles.cell} />
          </tr></tfoot>}
        </table>
      </div>
    </section>
  );
}

const GUIDED_MEDIAN_ROWS = Object.fromEntries(QUESTIONS.slice(0, 2).map((question) => [question.id, question.rows]));

function GuidedMedianQuestion({ questionId, progress, readOnly, onChange, onComplete }) {
  const [activeHint, setActiveHint] = useState(null);
  const hintDialog = useRef(null);
  const reduceMotion = useReducedMotion();
  const question = QUESTIONS.find((item) => item.id === questionId);
  const context = questionId === "q1"
    ? { value: "Books read last month", item: "books read" }
    : { value: "Number of pets", item: "pets" };
  const hints = [
    "Cumulative frequency is a running total of students. Start with the first frequency, then add the next row's frequency each time you move down the table.",
    "Add all the frequencies to find the total number of students. Write this in the Frequency column of the Total row. The final cumulative frequency gives the same total.",
    "The median is the middle of the ordered data. For an odd total, use (total frequency + 1) ÷ 2 to find its position. This tells you which student to look for, rather than the median value.",
    `Look down the cumulative frequency column for the first total that reaches or passes the median position. Read the ${context.item} from the first column in that row.`,
  ];
  useEffect(() => {
    const dialog = hintDialog.current;
    if (activeHint !== null && !dialog.open) dialog.showModal();
    if (activeHint === null && dialog.open) dialog.close();
  }, [activeHint]);
  const heading = (step, text) => (
    <div style={guidedStyles.stepHeading}>
      <h4 style={{ margin: 0 }}>{text}</h4>
      <button type="button" className="median-hint-button" aria-label={`Hint for ${text.slice(3).toLowerCase()}`}
        onClick={() => setActiveHint(step)} style={guidedStyles.hintButton} title="Show a hint">
        <svg aria-hidden="true" width="24" height="24" viewBox="0 -1 24 24" style={{ display: "block" }}>
          <path className="median-hint-bulb" d="M9 17h6v-2c0-2 3-3 3-7a6 6 0 00-12 0c0 4 3 5 3 7z" fill="#fde68a" stroke="currentColor" strokeWidth="1.7" />
          <path d="M9 20h6m-5 2h4M12 0v1M2 8h2m16 0h2M4 2l2 2m12 0l2-2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
  const rows = GUIDED_MEDIAN_ROWS[questionId];
  let runningTotal = 0;
  const cumulative = rows.map(([, frequency]) => (runningTotal += frequency));
  const position = (runningTotal + 1) / 2;
  const median = rows[cumulative.findIndex((frequency) => frequency >= position)][0];
  const expected = { ...Object.fromEntries(cumulative.map((value, index) => [`cf${index}`, value])), total: runningTotal, position, median };
  const state = progress || { step: 0, values: {}, status: {} };
  const steps = [rows.map((_, index) => `cf${index}`), ["total"], ["position"], ["median"]];
  const update = (key, value) => onChange({ ...state, values: { ...state.values, [key]: value }, status: { ...state.status, [key]: undefined }, message: "" });
  const check = () => {
    const status = { ...state.status };
    steps[state.step].forEach((key) => {
      const value = (state.values[key] || "").trim();
      status[key] = value !== "" && Number(value) === expected[key] ? "correct" : "incorrect";
    });
    const correct = steps[state.step].every((key) => status[key] === "correct");
    void playQuizFeedback(correct);
    const stepNames = ["Cumulative frequencies", "Total frequency", "Median position", "Median value"];
    const attempt = stepNames[state.step] + ": " + steps[state.step].map((key) =>
      (key.startsWith("cf") ? "Row " + (Number(key.slice(2)) + 1) : stepNames[state.step])
      + " = " + (state.values[key] || "Blank") + " (" + status[key] + ")").join("; ");
    const next = { ...state, status, attempts: [...(state.attempts || []), attempt], step: correct ? state.step + 1 : state.step,
      message: correct ? "Correct! This section is complete." : "Check the red answers and try again. Every answer must be correct to continue." };
    onChange(next);
    if (correct && state.step === 3) onComplete(String(median), next);
  };
  const input = (key, label, step) => (
    <input aria-label={label} inputMode="numeric" type="text" value={state.values[key] || ""}
      readOnly={readOnly || state.step > step || state.status[key] === "correct"}
      onChange={(event) => update(key, event.target.value)}
      aria-invalid={state.status[key] === "incorrect"}
      style={{ ...guidedStyles.input, ...(state.status[key] === "correct" ? guidedStyles.correct : state.status[key] === "incorrect" ? guidedStyles.incorrect : {}) }} />
  );
  const checkButton = (step) => !readOnly && state.step === step && (
    <button type="button" onClick={check} style={guidedStyles.button}>Check {step === 0 ? "cumulative frequencies" : step === 1 ? "frequency total" : step === 2 ? "median position" : "median"}</button>
  );
  const section = (step, children) => (
    <section style={guidedStyles.section}>
      {children}
      {state.step > step && <p style={guidedStyles.success}>✓ Correct</p>}
      {checkButton(step)}
    </section>
  );

  return (
    <div>
      <section style={guidedStyles.section}>
        {heading(0, "1. Complete the cumulative frequencies")}
        <div style={{ overflowX: "auto" }}>
          <table aria-label={`Students' ${context.item}`} style={guidedStyles.table}>
            <thead><tr>{[question.valueLabel, question.frequencyLabel, "Cumulative frequency"].map((label) => <th key={label} scope="col" style={guidedStyles.cell}>{label}</th>)}</tr></thead>
            <tbody>{rows.map(([value, frequency], index) => <tr key={value}>
              <th scope="row" style={guidedStyles.cell}>{value}</th><td style={guidedStyles.cell}>{frequency}</td>
              <td style={guidedStyles.cell}>{input(`cf${index}`, `Cumulative frequency for value ${value}`, 0)}</td>
            </tr>)}</tbody>
            {state.step >= 1 && <tfoot><tr>
              <th scope="row" style={guidedStyles.cell}>Total</th>
              <td style={guidedStyles.cell}>{input("total", "Total frequency", 1)}</td>
              <td style={guidedStyles.cell} />
            </tr></tfoot>}
          </table>
        </div>
        {checkButton(0)}
        {state.step === 1 && <div style={{ marginTop: 16 }}>
          {heading(1, "2. Fill in the total frequency")}
          {checkButton(1)}
        </div>}
      </section>
      {state.step >= 2 && section(2, <>
        {heading(2, "3. Find the median position")}
        <label>Position in the ordered data: {input("position", "Median position", 2)}</label>
      </>)}
      {state.step >= 3 && section(3, <>
        {heading(3, "4. Find the median value")}
        <label>Median number of {context.item}: {input("median", "Median value", 3)}</label>
      </>)}
      <p role="status" aria-live="polite">{state.message}</p>
      <style>{`
        .median-hint-dialog::backdrop { background: rgba(30, 27, 75, 0.38); }
        .median-hint-button { color: #92400e; background: #fffbeb; transition: background-color 150ms ease; }
        .median-hint-button:hover, .median-hint-button:focus-visible { background: #fde68a; }
      `}</style>
      <dialog ref={hintDialog} className="median-hint-dialog" aria-labelledby="median-hint-title"
        onCancel={() => setActiveHint(null)} onClose={() => setActiveHint(null)}
        onClick={(event) => { if (event.target === event.currentTarget) setActiveHint(null); }}
        style={guidedStyles.hintDialog}>
        {activeHint !== null && <motion.div key={activeHint} initial={reduceMotion ? false : { scale: 0.45, opacity: 0, rotate: -5 }}
          animate={{ scale: 1, opacity: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 320, damping: 19 }}
          style={guidedStyles.hintBubble}>
          <h3 id="median-hint-title" style={{ color: "#6d28d9", margin: "0 0 12px" }}>A little hint!</h3>
          <p style={{ fontSize: "1.1rem", lineHeight: 1.6, margin: "0 0 20px" }}>{hints[activeHint]}</p>
          <button type="button" autoFocus onClick={() => setActiveHint(null)} style={guidedStyles.button}>Got it!</button>
          <span aria-hidden="true" style={guidedStyles.bubbleTail} />
        </motion.div>}
      </dialog>
    </div>
  );
}

const guidedStyles = {
  stepHeading: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16 },
  hintButton: { display: "grid", placeItems: "center", padding: 0, boxSizing: "border-box", flexShrink: 0, width: 40, height: 40, borderRadius: "50%", border: "2px solid #f59e0b", cursor: "pointer", boxShadow: "0 3px 0 #fcd34d" },
  hintDialog: { border: 0, padding: 36, background: "transparent", maxWidth: 540, width: "calc(100% - 24px)", boxSizing: "border-box", overflow: "visible", fontFamily: "'Fredoka', cursive" },
  hintBubble: { position: "relative", padding: 28, border: "3px solid #7c3aed", borderRadius: "32px 32px 32px 8px", background: "#fffbeb", color: "#334155", boxShadow: "0 0 20px #c4b5fd, 0 0 44px rgba(167,139,250,0.7), 0 12px 0 rgba(109,40,217,0.18)" },
  bubbleTail: { position: "absolute", bottom: -17, left: 28, width: 26, height: 26, background: "#fffbeb", borderRight: "3px solid #7c3aed", borderBottom: "3px solid #7c3aed", transform: "rotate(45deg)" },
  section: { borderWidth: 2, borderStyle: "solid", borderColor: "#cbd5e1", borderRadius: 8, padding: 16, marginBottom: 16 },
  correct: { backgroundColor: "#e8f8ee", borderColor: "#42a96b" },
  incorrect: { backgroundColor: "#fdeaea", borderColor: "#d95858" },
  table: { borderCollapse: "collapse", width: "100%", border: "2px solid #334155", backgroundColor: "white", fontSize: "1.15rem" },
  cell: { border: "1px solid #334155", padding: "14px 12px", textAlign: "center" },
  input: { width: 70, maxWidth: "100%", boxSizing: "border-box", padding: 8, borderWidth: 2, borderStyle: "solid", borderColor: "#94a3b8", borderRadius: 5, fontSize: "1.1rem", textAlign: "center" },
  button: { display: "block", marginTop: 16, padding: "10px 16px", backgroundColor: "#0984e3", color: "white", border: 0, borderRadius: 6, cursor: "pointer", fontWeight: "bold" },
  success: { color: "#087f5b", fontWeight: "bold" },
};


// Generate short feedback tones locally, without loading audio files.
let quizAudioContext;
async function playQuizFeedback(correct) {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    if (!quizAudioContext || quizAudioContext.state === "closed") quizAudioContext = new AudioContextClass();
    if (quizAudioContext.state === "suspended") await quizAudioContext.resume();
    const context = quizAudioContext;
    const start = context.currentTime;
    const oscillator = context.createOscillator();
    const volume = context.createGain();
    oscillator.type = correct ? "sine" : "triangle";
    oscillator.frequency.setValueAtTime(correct ? 880 : 240, start);
    if (correct) oscillator.frequency.setValueAtTime(1174.66, start + 0.07);
    else {
      oscillator.frequency.exponentialRampToValueAtTime(170, start + 0.1);
      oscillator.frequency.setValueAtTime(210, start + 0.14);
      oscillator.frequency.exponentialRampToValueAtTime(130, start + 0.28);
    }
    const duration = correct ? 0.2 : 0.3;
    volume.gain.setValueAtTime(0.0001, start);
    volume.gain.exponentialRampToValueAtTime(correct ? 0.15 : 0.14, start + 0.008);
    if (!correct) {
      volume.gain.exponentialRampToValueAtTime(0.0001, start + 0.11);
      volume.gain.setValueAtTime(0.0001, start + 0.14);
      volume.gain.exponentialRampToValueAtTime(0.14, start + 0.15);
    }
    volume.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(volume);
    volume.connect(context.destination);
    oscillator.onended = () => { oscillator.disconnect(); volume.disconnect(); };
    oscillator.start(start);
    oscillator.stop(start + duration + 0.01);
  } catch {
    // Audio availability should never interrupt answer checking.
  }
}
